<template>
  <div>
    <v-app-bar app class="home-nav-bar" light flat >
      <v-container>
        <v-row>
          <div class="d-flex align-center logo-container">
            <v-img
              alt="Decidedly Logo"
              class="shrink mr-2"
              contain
              :src="require('../assets/logo-blue.svg')"
              transition="scale-transition"
              width="35"
            />
            Decidedly
          </div>

          <v-spacer></v-spacer>

          <v-btn
            v-if="authenticated"
            outlined
            color="primary"
            @click="gotoDashboard"
          >
            <span class="mr-2">Dashboard</span>
          </v-btn>
          <v-btn
            v-else
            href="/login"
            outlined
            color="primary"
          >
            <span class="mr-2">Sign in</span>
          </v-btn>
        </v-row>
      </v-container>
    </v-app-bar>
    <v-container fluid class="home-page">
      <v-row no-gutters align="start" justify="center" class="info-section" :class="backgroundClass">
        <v-col >
          <v-container class="mb-6">
            <v-row justify="center" class="pt-0">
              <v-col cols="12" lg="4" class="pt-0">
                <div class="px-4">
                  <div class="text-right">
                    <h1>Organized</h1>
                    <h1>Smart</h1>
                    <h1>Sharing</h1>
                  </div>
                  <div class="d-none d-md-block">
                    <p class="mt-2 product-description text-right">
                      Decidedly is designed to help you organize and compare anything you want to decide between
                    </p>
                    <p class="product-description text-right">
                      It makes it easy to find and build options, and helps you connect with others when you need help making the right decision
                    </p>
                    <p class="product-description text-right">
                      It's the comparison and review platform for anything and everything
                    </p>
                  </div>
                  <div class="d-md-none">
                    <p class="product-description text-right mt-4">
                      The comparison and review platform for anything and everything
                    </p>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" lg="4">
                <v-card class="elevation-12" v-if="registered || alreadyRegistered">
                  <v-card-text>
                    <div>
                      <h1>Thanks!</h1>
                      <p class="mt-6 alpha-program-info">
                        Thank you for your interest in joining our alpha program.
                      </p>
                      <p class="mb-6 alpha-program-info" v-if="alreadyRegistered">
                        The email <strong>{{registerEmail}}</strong> is <strong>already registered and being processed</strong>. We will send instructions should you be added to the Alpha program. Thank you for your patience.
                      </p>
                      <p class="mb-6 alpha-program-info" v-if="registered">
                        We will be in touch via email to let you know if you have been accepted to participate and provide instructions to finish the registration process.
                      </p>
                    </div>
                  </v-card-text>
                </v-card>
                <v-form @submit.prevent="validateRegisterForm" v-else>
                  <v-card class="elevation-12">
                    <v-card-text>
                      <div>
                        <h1>Join Decidedly Alpha</h1>
                        <p class="mt-6 alpha-program-info">
                          Decidedly is currently in closed alpha and available on an invite basis only
                        </p>
                        <p class="mb-6 alpha-program-info">
                          To join the alpha program register your interest using your email address*
                        </p>
                      </div>
                      <v-text-field
                        v-model="registerEmail"
                        :error-messages="registerEmailErrors"
                        outlined
                        label="Your email address"
                        name="email"
                        type="email"
                        @blur="$v.registerEmail.$touch()"
                      ></v-text-field>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn
                        block
                        type="submit"
                        large
                        color="success"
                        @click="$v.registerEmail.$touch()"
                      >
                        {{registering ? 'Registering...' : 'Register Interest'}}
                      </v-btn>
                    </v-card-actions>
                    <div class="pa-6 text-caption">
                      By clicking <strong>Register Interest</strong>, you agree to our <router-link to="/terms" target="_blank">Terms of Service</router-link> and <router-link to="/privacy" target="_blank">Privacy Statement</router-link>. We can occasionally send you account related emails.
                    </div>
                  </v-card>
                  <v-container class="mb-6 alpha-warn">
                    <v-row justify="center" class="pt-0">
                      <v-col cols="1" class="d-flex justify-center">
                        <div>*</div>
                      </v-col>
                      <v-col cols="11" class="text-caption">
                        Please note that the alpha program is currently full and we have paused registering new accounts. You can still register your email above if you wish to be invited to join at a later date when we start our next intake.
                      </v-col>
                    </v-row>
                  </v-container>
                </v-form>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
  // This import loads the firebase namespace along with all its type information.
  import * as firebase from 'firebase/app'

  // Load the auth feature into the firebase namespace.
  import 'firebase/auth'

  import Constants from '../constants.json'
  import axios from 'axios'
  import { validationMixin } from 'vuelidate'
  import { required, email } from 'vuelidate/lib/validators'

  export default {
    name: 'Register',
    components: {
    },
    mixins: [validationMixin],
    data () {
      return {
        registering: false,
        registerEmail: null,
        registered: false,
        alreadyRegistered: false
      }
    },
    validations: {
      registerEmail: { required, email }
    },
    computed: {
      authenticated () {
        return firebase.auth().currentUser && firebase.auth().currentUser.emailVerified
      },
      registerEmailErrors () {
        const errors = []
        if (!this.$v.registerEmail.$dirty) return errors
        !this.$v.registerEmail.email && errors.push('Must be valid email')
        !this.$v.registerEmail.required && errors.push('Email is required.')
        return errors
      },
      backgroundClass () {
        switch (this.$vuetify.breakpoint.name) {
          case 'xs': return 'section-sm'
          case 'sm': return 'section-sm'
          case 'md': return 'section-lg'
          case 'lg': return 'section-lg'
          case 'xl': return 'section-xl'
          default: return ''
        }
      }
    },
    methods: {
      gotoDashboard: function () {
        this.$router.replace('/decisions')
      },
      validateRegisterForm: function () {
        // console.log(`Validate register called`)
        if (this.registerEmail && !this.$v.registerEmail.$invalid) {
          this.register()
        }
      },
      register: async function () {
        this.registering = true
        this.registered = false
        this.alreadyRegistered = false
        // console.log(`TODO Register`)
        ///users/register/alpha
        const postRegisterAlphaURL = `${Constants.API_BASE_PATH}/users/register/alpha`
        const body = {
          email: this.registerEmail
        }
        // console.log(`Calling post register user ${JSON.stringify(body)}`)
        const registerResponse = await axios.post(postRegisterAlphaURL, body, {
          headers: {
            'Content-Type': 'application/json'
          }
        })

        if (registerResponse && registerResponse.data && registerResponse.data.status === 'success') {
          this.registered = true
          this.registerEmail = null
          this.$v.$reset()
        } else if (registerResponse && registerResponse.data && registerResponse.data.status === 'exists') {
          this.registered = false,
          this.alreadyRegistered = true
        }

        this.registering = false
      }
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">

.home-page {
  font-size: 1.4rem;
  padding: 0;
}

.home-nav-bar {
  background-color: #FFF !important;
  color: #222;

  .logo-container {
    color: #1e40aa;
    text-transform: uppercase;
  }
}

.info-section {
  background: #0e2887;
  background-image: linear-gradient(#3a52b5, #0e2887);
  color: white;
  padding-top: 40px;
  /*#3a52b5*/

  // &.section-lg {
  //   /*background-image: url("IMAGE_URL"); fallback */
  //   background: url('../assets/newbagkround.png') repeat-y right top, linear-gradient(#3a52b5, #0e2887); /* W3C */
  //   background-size: 100% auto;
  // }
  // &.section-xl {
  //   /*background-image: url("IMAGE_URL"); fallback */
  //   background: url('../assets/newbagkround.png') repeat-y right top, linear-gradient(#3a52b5, #0e2887); /* W3C */
  //   background-size: 70% auto;
  // }
  &.section-sm {
    padding-top: 10px;
    h1 {
      font-size: 1.9rem;
    }
  }
  /*background-image: linear-gradient(#3a52b5, #0e2887);*/

  .product-description {
    color: #EEE;
    font-size: 1.3rem;
  }

  .alpha-program-info {
    font-size: 1.2rem;
  }

  .alpha-warn {
    margin-top: -4px;
    background: #607D8B;
    color: #FFF;
  }
}

</style>
